import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEO, { organizationSchema, createFAQSchema } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Check, Star, Layers, Building2 } from "lucide-react";
import { Link } from "react-router-dom";

const FluxVsAdobeFirefly = () => {
  const faqs = [
    {
      question: "What is Adobe Firefly best for?",
      answer: "Adobe Firefly excels at integration with Creative Cloud apps (Photoshop, Illustrator), commercially safe imagery (trained on licensed content), and professional design workflows. It's ideal for enterprise teams already using Adobe."
    },
    {
      question: "Is FLUX better than Adobe Firefly for bulk generation?",
      answer: "Yes, FLUX is significantly better for bulk generation with 1000+ images per hour capability. Adobe Firefly is designed for individual creative workflows within Adobe apps, not mass production."
    },
    {
      question: "Which has better commercial licensing?",
      answer: "Both offer commercial rights, but differently. Adobe Firefly is trained exclusively on licensed content (Adobe Stock, public domain) making it 'IP safe'. FLUX also offers full commercial rights on paid plans."
    },
    {
      question: "Can I use Firefly without Creative Cloud?",
      answer: "Adobe Firefly has a standalone web app, but its best features integrate with Photoshop and Illustrator. FLUX is a standalone platform requiring no additional software."
    },
    {
      question: "Which is better for product photography?",
      answer: "For bulk product photography, FLUX is superior with its speed and volume capabilities. Firefly is better for detailed retouching and editing within existing Photoshop workflows."
    }
  ];

  const comparison = [
    { feature: "Bulk Generation", flux: "1000+/hour", firefly: "Limited", winner: "flux" },
    { feature: "Adobe Integration", flux: "None", firefly: "Excellent", winner: "firefly" },
    { feature: "Standalone Use", flux: "Full Platform", firefly: "Web App", winner: "flux" },
    { feature: "IP Safety", flux: "Commercial Rights", firefly: "Licensed Training", winner: "firefly" },
    { feature: "Speed", flux: "Fast", firefly: "Moderate", winner: "flux" },
    { feature: "Photorealism", flux: "Excellent", firefly: "Very Good", winner: "flux" },
    { feature: "Price", flux: "$0-49/mo", firefly: "$20-80/mo (CC)", winner: "flux" },
    { feature: "Enterprise Features", flux: "Available", firefly: "Excellent", winner: "firefly" },
  ];

  return (
    <div className="min-h-screen w-full overflow-x-hidden">
      <SEO
        title="FLUX vs Adobe Firefly 2026 | AI Image Generator Comparison"
        description="FLUX vs Adobe Firefly comparison. Which AI is better for your needs? Complete analysis of bulk generation, Adobe integration, commercial rights & pricing."
        keywords="flux vs adobe firefly, firefly ai comparison, adobe ai vs flux, creative cloud ai, commercial ai images, ip safe ai generator, photoshop ai vs flux"
        url="/flux-vs-adobe-firefly"
        jsonLd={[
          organizationSchema,
          createFAQSchema(faqs)
        ]}
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'FLUX vs Adobe Firefly', url: '/flux-vs-adobe-firefly' }
        ]}
      />
      
      <Header />
      
      <main className="w-full">
        {/* Hero Section */}
        <section className="relative py-20 lg:py-28 bg-gradient-to-br from-background via-background to-primary/5">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Star className="w-4 h-4 fill-current" />
                Updated January 2026
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
                FLUX vs Adobe Firefly
                <span className="block text-primary mt-2">2026 Comparison</span>
              </h1>
              
              <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
                FLUX dominates bulk generation and standalone use. Adobe Firefly excels in Creative Cloud integration. 
                Which fits your workflow?
              </p>
            </div>
          </div>
        </section>

        {/* Quick Verdict */}
        <section className="py-12 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-6">
              <div className="bg-card border rounded-xl p-6 text-center">
                <h3 className="text-xl font-bold mb-2">Choose FLUX If:</h3>
                <ul className="text-left space-y-2 mt-4">
                  <li className="flex items-center gap-2">
                    <Check className="w-5 h-5 text-green-500" />
                    <span>Bulk generation is needed</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-5 h-5 text-green-500" />
                    <span>You don't use Adobe CC</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-5 h-5 text-green-500" />
                    <span>E-commerce volume matters</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-5 h-5 text-green-500" />
                    <span>Cost efficiency is priority</span>
                  </li>
                </ul>
                <Button asChild className="mt-6">
                  <Link to="/try-generator">Try FLUX Free</Link>
                </Button>
              </div>
              
              <div className="bg-card border rounded-xl p-6 text-center">
                <h3 className="text-xl font-bold mb-2">Choose Firefly If:</h3>
                <ul className="text-left space-y-2 mt-4">
                  <li className="flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-red-500" />
                    <span>Heavy Adobe CC user</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-red-500" />
                    <span>Enterprise IP concerns</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-red-500" />
                    <span>Photoshop workflow</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-red-500" />
                    <span>Already paying for CC</span>
                  </li>
                </ul>
                <Button variant="outline" className="mt-6" disabled>
                  Visit Adobe Firefly
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Comparison Table */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">Feature Comparison</h2>
            
            <div className="max-w-4xl mx-auto overflow-x-auto">
              <table className="w-full bg-card border rounded-xl">
                <thead>
                  <tr className="border-b">
                    <th className="p-4 text-left">Feature</th>
                    <th className="p-4 text-center">FLUX</th>
                    <th className="p-4 text-center">Adobe Firefly</th>
                    <th className="p-4 text-center">Winner</th>
                  </tr>
                </thead>
                <tbody>
                  {comparison.map((row, index) => (
                    <tr key={index} className="border-b">
                      <td className="p-4 font-medium">{row.feature}</td>
                      <td className={`p-4 text-center ${row.winner === 'flux' ? 'text-green-600 font-semibold' : ''}`}>
                        {row.flux}
                      </td>
                      <td className={`p-4 text-center ${row.winner === 'firefly' ? 'text-red-600 font-semibold' : ''}`}>
                        {row.firefly}
                      </td>
                      <td className="p-4 text-center">
                        {row.winner === 'flux' && <span className="bg-green-100 text-green-700 px-2 py-1 rounded text-sm">FLUX</span>}
                        {row.winner === 'firefly' && <span className="bg-red-100 text-red-700 px-2 py-1 rounded text-sm">Firefly</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-12">Frequently Asked Questions</h2>
              
              <div className="space-y-6">
                {faqs.map((faq, index) => (
                  <div key={index} className="bg-card border rounded-lg p-6">
                    <h3 className="font-semibold text-lg mb-2">{faq.question}</h3>
                    <p className="text-muted-foreground">{faq.answer}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold mb-4">Ready to Try FLUX?</h2>
            <p className="text-muted-foreground mb-8 max-w-xl mx-auto">
              No Adobe subscription needed. Bulk generate images instantly with FLUX.
            </p>
            <Button asChild size="lg" className="text-lg px-8">
              <Link to="/try-generator">Try FLUX Free Now</Link>
            </Button>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default FluxVsAdobeFirefly;
